// chatTimeline.js — pure merge of the durable SessionChannel stream + optimistic local user turns
// into ONE monotonic, seq-ordered conversation timeline. No React, no streaming — node-testable.
//
// Durable axis: TEXT_MESSAGE (role:user) → user turn keyed by seq; TEXT_MESSAGE_CONTENT → assistant
// turn coalesced by payload.messageId, keyed by its FIRST seq. Both share the one monotonic seq axis.
// Optimistic axis: a local user turn (zero-latency echo) carries the client message_id UUID; it is
// DROPPED when its durable echo (matching UUID) arrives, else ANCHORED by submit order — inserted
// before the first assistant turn that has no durable user echo ahead of it. This fixes the new-chat
// race (bug a): when the only durable turn is a fast assistant reply (no durable user echo yet), a
// user turn typed BEFORE it must still render ABOVE it, not blind-appended below the durable tail.

// CHAT-1 / CHAT-6 production seam: the JSX (AgentStudioChat) appends optimistic user turns as
// { role, text, ref, messageId, anchorSeq } — anchorSeq is the MAX DURABLE SEQ present at send
// time (stamped at the call site, analogous to how submitIndex was). This helper carries that
// anchorSeq through and ALSO stamps submitIndex = the turn's array position (back-compat: submit
// order). mergeTimeline anchors on anchorSeq (correct for every turn), falling back to submitIndex
// only for the legacy first-turn race. Pure + exported = node-testable.
import { resolvedInteractionIdSet } from "./interactionResolution.js"

export function toOptimisticUserTurns(userTurns = []) {
  return userTurns.map((t, i) => ({ ...t, submitIndex: i }))
}

// LOOP D2(b): shape the reducer's `liveDeltas` ({ messageId: text }, insertion-ordered = first-
// appearance order) into the `liveTurns` mergeTimeline consumes. `anchorByMessageId` is the caller's
// record of the max durable seq present when each live messageId FIRST appeared (the chronological
// anchor); `fallbackAnchorSeq` (typically the current max durable seq) is used for any unrecorded id.
export function liveTurnsFromDeltas(liveDeltas = {}, anchorByMessageId = {}, fallbackAnchorSeq = 0) {
  return Object.entries(liveDeltas).map(([messageId, text]) => ({
    messageId,
    text,
    anchorSeq: anchorByMessageId[messageId] != null ? anchorByMessageId[messageId] : fallbackAnchorSeq,
  }))
}

export function dedupKeyOf(envelope) {
  const p = envelope && envelope.payload
  // payload.messageId (S4-1 Rails edit) is primary; envelope.event_id is the self-describing fallback.
  return (p && p.messageId) || (envelope && envelope.event_id) || null
}

// Durable TEXT_MESSAGE_CONTENT rows are complete assistant text blocks in Vesta's AG-UI contract;
// token deltas live only on the ephemeral streaming lane. A provider may emit more than one text
// block for the same assistant message (observed with Sonnet 4.6 Low). Coalesce those blocks without
// replaying an identical body, and preserve a readable paragraph boundary between distinct bodies.
export function appendAssistantBody(existing = "", body = "") {
  if (body === "") return existing
  if (existing === "") return body
  if (body === existing) return existing
  if (/\s$/.test(existing) || /^\s/.test(body)) return existing + body
  return `${existing}\n\n${body}`
}

// LOOP D2(b) — fold the ephemeral live typewriter lane into ONE ordered timeline.
//
// Pre-fix, live bubbles rendered in a SEPARATE, seq-less, always-last lane (a concat after the
// merged durable turns in AgentStudioChat). A bubble's on-screen position depended on which lane it
// was in, not chronology — so it leapt when it settled, and a durable-less reply (Bug A) was stranded
// at the bottom UNDER later replies (DEFECT #3 Bug B, out-of-order settling).
//
// Each live bubble carries a synthetic `anchorSeq` = the MAX DURABLE SEQ present when it FIRST
// appeared — the SAME anchoring the optimistic user turns already use (proven by the ordering-lock
// suite). It is inserted into the single sorted timeline AFTER the last durable turn with seq <=
// anchorSeq, so it occupies its chronological slot whether still live or already durable; settling is
// an in-place swap with no positional jump. A live bubble whose messageId already settled durable is
// dropped (the durable body supersedes — belt-and-suspenders; the reducer usually clears it first).
//
// `liveTurns`: [{ messageId, text, anchorSeq }]. Marked `live:true` so the render uses the streaming
// lane (StreamingMarkdown, .st-msg--live) instead of the settled markdown + Copy button.
export function mergeTimeline({ durableEvents = [], optimisticUserTurns = [], liveTurns = [] } = {}) {
  const turns = []
  const assistant = {}            // messageId -> coalesced assistant turn
  const seenUserKeys = new Set()  // UUIDs of durable user turns (to dedup optimistic echoes)

  // P4 — which interaction_ids have a durable resolution (a TOOL_CALL_RESULT carrying it)? An
  // interaction directive flips to read-only the instant its resolution lands in the stream (the
  // approve POST resumes the Session → a TOOL_CALL_RESULT broadcasts → the card settles). Same rule
  // interactionComponent.reduceInteractions uses; pre-scanned here so the single render pass below can
  // mark each interaction directive resolved/unresolved. X1: the rule itself now lives in ONE shared
  // module (interactionResolution.js) that Items A/B/G also import — do not re-derive it.
  const resolvedInteractionIds = resolvedInteractionIdSet(durableEvents)

  for (const e of durableEvents) {
    if (e.type === "TEXT_MESSAGE" && e.payload && e.payload.role === "user") {
      const key = dedupKeyOf(e)
      if (key != null) seenUserKeys.add(key)
      // Task 5: carry the durable payload.attachments onto the user turn so the bubble can render
      // image thumbnails / PDF chips (durable replay works free — the attachments live in the
      // replayed payload). Copied ONLY when present so a no-attachment turn stays shape-identical.
      const attachments = Array.isArray(e.payload.attachments) ? e.payload.attachments : null
      turns.push({
        role: "user", text: e.payload.content || "", messageId: key, seq: e.seq,
        ...(attachments ? { attachments } : {}),
      })
    } else if (e.type === "TEXT_MESSAGE_CONTENT") {
      const p = e.payload || {}
      const mid = p.messageId
      if (mid == null) continue
      const chunk = p.delta != null ? p.delta : p.content != null ? p.content : ""
      if (!assistant[mid]) {
        assistant[mid] = { role: "assistant", text: "", messageId: mid, seq: e.seq }
        turns.push(assistant[mid])
      }
      assistant[mid].text = appendAssistantBody(assistant[mid].text, chunk)
    } else if (e.type === "CUSTOM") {
      // G5 (#22): durable CUSTOM directives get an in-thread home as `directive` turns. NO reducer
      // change — CUSTOM events are already retained in eventsBySeq; this is the ONLY surfacing site.
      // The persisted client envelope flattens the AG-UI `name` onto `e.payload.name` (see the
      // interaction CUSTOM shape in interactionComponent.node-test.js). A report carries its render
      // payload as `e.payload.value.render` (per the report-spine contract), falling back to
      // `e.payload.render` if a flatter envelope ever lands. An interaction is surfaced as a
      // read-only directive (fixes the previously-invisible approval card gap).
      const p = e.payload || {}
      if (p.name === "report") {
        const render = (p.value && p.value.render) != null ? p.value.render : p.render
        turns.push({ role: "directive", kind: "report", render, seq: e.seq, messageId: dedupKeyOf(e) })
      } else if (p.name === "interaction") {
        const render = (p.value && p.value.render) != null ? p.value.render : p.render
        // P4 — DUAL-SURFACE APPROVE: carry the per-viewer capability the live observe-stream
        // serializer (SessionChannel#replay → Agent::InteractionEventCapability) minted onto this
        // event's payload for the bound viewer. With a token present and no durable resolution yet,
        // AgentStudioChat renders an ACTIONABLE Approve/Decline card (the chat half of "approve from
        // EITHER surface"); token-absent (non-bound viewer) or already-resolved → read-only.
        turns.push({
          role: "directive", kind: "interaction", render, seq: e.seq, messageId: dedupKeyOf(e),
          interaction_id: p.interaction_id || null,
          requiresCapability: p.requires_capability === true,
          capability: (p.capability && p.capability.token) || null,
          capabilityAction: (p.capability && p.capability.action) || null,
          resolved: resolvedInteractionIds.has(p.interaction_id),
          // P2 — the approve-then-apply FAILURE stamp (Agent::ApplyFailureStamp), merged onto this
          // payload at serialization time off MoneyAction#card. Present ONLY when a human's approve
          // reached Meta and failed; the card correctly stays unresolved and still capability-bearing
          // (nothing was consumed, a retry is the point), so without this the chat card would keep
          // rendering a plain "Approve" as if the click had never happened.
          applyFailure: p.apply_failure || null,
        })
      }
    }
  }

  // Durable turns sort by their monotonic seq (assistant turns keep their FIRST seq).
  turns.sort((a, b) => a.seq - b.seq)

  // Surviving optimistic turns (echo not yet arrived) anchor by the MAX DURABLE SEQ that existed
  // when the turn was submitted (anchorSeq), NOT by a durable-user count. A pending user turn
  // belongs immediately AFTER every durable turn that already existed at submit time, and BEFORE
  // anything that arrived after it. The clean rule: insert before the first durable turn whose
  // seq > anchorSeq (i.e. after the last durable turn with seq <= anchorSeq).
  //
  //   • First-turn race (CHAT-1): the turn was submitted when ZERO durable turns existed, so
  //     anchorSeq = 0. A later fast assistant reply (seq=5) has seq > 0 → the pending turn anchors
  //     ABOVE it (the live screenshot bug, now correct).
  //   • 2nd+ turn (CHAT-6): durable=[u1(seq1), a1(seq2)] existed at submit time, so anchorSeq = 2.
  //     The pending follow-up anchors AFTER a1(seq2) → [u1, a1, u2(pending)] (was [u1, u2, a1]).
  //
  // Back-compat: submitIndex (the legacy CHAT-1 stamp) is honored ONLY as the zero-value
  // first-turn-race signal when anchorSeq is absent — submitIndex 0 ⇒ anchorSeq 0 (anchor above a
  // later assistant). Any other legacy submitIndex with no anchorSeq, or no signal at all, blind-
  // appends after the durable tail (preserves the AC3 pending-last contract).
  for (const t of optimisticUserTurns) {
    if (t.messageId != null && seenUserKeys.has(t.messageId)) continue // echo arrived → drop optimistic
    // Fix #4 — carry the failure flags onto the optimistic turn so a "couldn't send" affordance renders
    // ANCHORED to its own user bubble (via anchorSeq below, not always-last) AND auto-clears when the
    // durable echo arrives (the seenUserKeys drop at the top of this loop removes the whole pending turn).
    const pending = { role: "user", text: t.text, messageId: t.messageId, ref: t.ref || null, pending: true,
      status: t.status, error: t.error,
      // Task 5: carry the optimistic turn's attachments so the bubble shows thumbnails immediately
      // (before the durable echo arrives); auto-dropped with the whole pending turn on echo.
      ...(Array.isArray(t.attachments) ? { attachments: t.attachments } : {}) }

    let anchorSeq
    if (t.anchorSeq != null) {
      anchorSeq = t.anchorSeq                 // primary: max durable seq at submit time
    } else if (t.submitIndex === 0) {
      anchorSeq = 0                           // legacy first-turn race → anchor above a later reply
    } else {
      turns.push(pending)                     // no anchoring signal → append after the durable tail
      continue
    }

    // Insert AFTER the last durable turn with seq <= anchorSeq (before the first with seq > it).
    let insertAt = turns.length
    for (let i = 0; i < turns.length; i++) {
      if (turns[i].seq != null && turns[i].seq > anchorSeq) { insertAt = i; break }
    }
    turns.splice(insertAt, 0, pending)
  }

  // ── D2(b): fold the live ephemeral lane into the one ordered timeline (see header above) ──
  for (const lt of liveTurns) {
    if (lt == null || lt.messageId == null) continue
    if (assistant[lt.messageId]) continue // durable body already present → drop the live duplicate
    const live = { role: "assistant", text: lt.text, messageId: lt.messageId, live: true }
    const anchorSeq = lt.anchorSeq != null ? lt.anchorSeq : Number.MAX_SAFE_INTEGER
    // Insert AFTER the last SEQ'd (durable) turn with seq <= anchorSeq. Non-seq turns (pending
    // users, other live bubbles) are transparent to this boundary, so the live bubble lands in its
    // chronological durable slot; ties preserve first-appearance order (liveTurns is in that order).
    let insertAt = turns.length
    for (let i = 0; i < turns.length; i++) {
      if (turns[i].seq != null && turns[i].seq > anchorSeq) { insertAt = i; break }
    }
    turns.splice(insertAt, 0, live)
  }
  return turns
}
